import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import type { ApiResponse } from '@shared-models';
import { API_CONFIG, ApiConfig } from '../../api-config';

export interface CompanyServiceStatusShippingConfigItem {
  statusId: string;
  statusCode?: string;
  statusName?: string;
  enabled: boolean;
  shippingMethodIds: string[];
}

export interface CompanyServiceShippingConfig {
  companyId: string;
  serviceId: string;
  shippingMethodIds: string[];
  statuses: CompanyServiceStatusShippingConfigItem[];
}

export interface CompanyServiceShippingConfigUpsertPayload {
  shippingMethodIds: string[];
  statuses: Array<{
    statusId: string;
    enabled: boolean;
    shippingMethodIds: string[];
  }>;
}

/**
 * Drawer «Configurar servicio»: formas del servicio + estatus×formas.
 *
 * GET/PUT `/api/structure/companies/{companyId}/services/{serviceId}/shipping-config`
 */
@Injectable({ providedIn: 'root' })
export class CompanyServiceShippingConfigService {
  private readonly httpClient = inject(HttpClient);
  private readonly apiConfig = inject<ApiConfig>(API_CONFIG);

  private url(companyId: string, serviceId: string): string {
    return `${this.apiConfig.apiUrl}/structure/companies/${companyId}/services/${serviceId}/shipping-config`;
  }

  get(
    companyId: string,
    serviceId: string,
  ): Observable<CompanyServiceShippingConfig> {
    return this.httpClient
      .get<ApiResponse<CompanyServiceShippingConfig>>(
        this.url(companyId, serviceId),
      )
      .pipe(map((response) => this.normalize(response.data, companyId, serviceId)));
  }

  update(
    companyId: string,
    serviceId: string,
    payload: CompanyServiceShippingConfigUpsertPayload,
  ): Observable<CompanyServiceShippingConfig> {
    return this.httpClient
      .put<ApiResponse<CompanyServiceShippingConfig>>(
        this.url(companyId, serviceId),
        {
          shippingMethodIds: payload.shippingMethodIds ?? [],
          statuses: payload.statuses ?? [],
        },
      )
      .pipe(
        map((response) =>
          this.normalize(response.data, companyId, serviceId),
        ),
      );
  }

  private normalize(
    data: CompanyServiceShippingConfig | undefined,
    companyId: string,
    serviceId: string,
  ): CompanyServiceShippingConfig {
    if (!data) {
      return {
        companyId,
        serviceId,
        shippingMethodIds: [],
        statuses: [],
      };
    }
    return {
      companyId: data.companyId || companyId,
      serviceId: data.serviceId || serviceId,
      shippingMethodIds: Array.isArray(data.shippingMethodIds)
        ? data.shippingMethodIds.map(String)
        : [],
      statuses: Array.isArray(data.statuses)
        ? data.statuses.map((s) => ({
            statusId: String(s.statusId),
            statusCode: s.statusCode,
            statusName: s.statusName,
            enabled: !!s.enabled,
            shippingMethodIds: Array.isArray(s.shippingMethodIds)
              ? s.shippingMethodIds.map(String)
              : [],
          }))
        : [],
    };
  }
}
